import type {
  AttributeResponse,
  PlayerResponse,
  RecStatusResponse,
} from '@on3/api';
import { Check } from '@on3/ui-lib/components/Svg';
import { useSite } from '@on3/ui-lib/index';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { TeamAvatar } from 'components/Image/TeamAvatar';
import { ConditionalLink } from 'components/UI/StarRating/StarRating';
import { Text } from 'components/UI/Typography/Text';
import { format } from 'date-fns';
import { useMemo } from 'react';

import styles from './CommittedStatus.module.scss';

interface ICommittedStatusV2 {
  player?: PlayerResponse;
  status: RecStatusResponse;
  sport?: AttributeResponse;
}
export const CommittedStatus = ({
  status,
  player,
  sport,
}: ICommittedStatusV2) => {
  const { classYear } = player || {};

  const { type, committedAsset, date, recruitmentYear } = status ?? {};
  const { defaultYears, sportsData } = useSite();
  const { footballRecruitingYear, nonFootballRecruitingYear } = defaultYears;
  const recruitingYear =
    sport?.key === 1 ? footballRecruitingYear : nonFootballRecruitingYear;
  const commitDate = date && format(new Date(date), 'M/dd/yy');
  const isSigned = type === 'Signed' || type === 'Enrolled';
  const isCommitted = type === 'Committed';
  const commitBackground = useMemo(
    () => ({
      backgroundColor: committedAsset?.primaryColor ?? '',
    }),
    [committedAsset?.primaryColor],
  );
  const convertedRecruitingYear = parseInt(recruitingYear.toString(), 10);

  const commitYear =
    classYear && convertedRecruitingYear >= classYear
      ? convertedRecruitingYear
      : classYear;
  const useLink = !!committedAsset?.slug && (!!recruitmentYear || !!commitYear);

  const isIndustrySport = sportsData?.find((sportData) => {
    return slugify(sportData.name) === sport?.name;
  })?.isIndustryRankable;

  const commitLink = isIndustrySport
    ? 'industry-comparison-commits'
    : 'commits';

  return (
    <ConditionalLink
      className={styles.playerStatusContainer}
      condition={useLink}
      href={`/college/${committedAsset?.slug}/${slugify(
        sport?.name || 'football',
      )}/${recruitmentYear ?? commitYear}/${commitLink}/`}
    >
      <div className={styles.commitStatus} style={commitBackground}>
        <span className={styles.logoWrapper}>
          <TeamAvatar
            className={styles.commitLogo}
            height={25}
            src={committedAsset?.assetUrl}
            width={25}
          />
        </span>
        {isCommitted && (
          <Text className={styles.status} variant="subtitle1">
            Hard Commit
          </Text>
        )}
        {isSigned && (
          <div className={styles.signedContainer}>
            <div className={styles.checkmarkContainer}>
              <Check className={styles.checkmarkIcon} />
            </div>
            <Text className={styles.signed} variant="subtitle1">
              {type}
            </Text>
          </div>
        )}
      </div>

      {isCommitted && (
        <Text className={styles.statusText} variant="subtitle1">
          Hard Commit
        </Text>
      )}
      {isSigned && (
        <Text className={styles.signedText} variant="subtitle1">
          {type}
        </Text>
      )}
      <Text className={styles.commitDate}>{commitDate}</Text>
    </ConditionalLink>
  );
};
